import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  validateOnBlur?: boolean;
  /** Emit raw digits */
  unmasked?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CreditCardInput',
  props: {
    modelValue: { default: "" },
    placeholder: { default: "ACCT-000003" },
    disabled: { type: Boolean, default: false },
    size: { default: "middle" },
    allowClear: { type: Boolean, default: true },
    status: { default: "" },
    validateOnBlur: { type: Boolean, default: true },
    unmasked: { type: Boolean, default: true }
  },
  emits: ["update:modelValue", "change", "blur", "pressEnter", "validate", "brand"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const displayValue = ref("");

const onlyDigits = (value: string) => value.replace(/\D/g, "").slice(0, 16);

const formatCard = (digits: string) => digits.replace(/(.{4})/g, "$1 ").trim();

const detectBrand = (digits: string) => {
  if (/^4/.test(digits)) return "visa";
  if (/^5[1-5]/.test(digits) || /^2(2|7)/.test(digits)) return "mastercard";
  if (/^3[47]/.test(digits)) return "amex";
  if (/^6(011|5)/.test(digits)) return "discover";
  if (/^(62|81)/.test(digits)) return "unionpay";
  if (/^9704/.test(digits)) return "napas";
  return "card";
};

const brandIcon = computed(() => {
  const brand = detectBrand(onlyDigits(props.modelValue ?? ""));
  const map: Record<string, string> = {
    visa: "fab fa-cc-visa",
    mastercard: "fab fa-cc-mastercard",
    amex: "fab fa-cc-amex",
    discover: "fab fa-cc-discover",
    card: "fas fa-credit-card",
    napas: "fas fa-credit-card",
    unionpay: "fas fa-credit-card",
  };
  return map[brand] || map.card;
});

/** Luhn algorithm */
const isValidLuhn = (digits: string) => {
  if (!digits) return true;
  if (digits.length < 13 || digits.length > 16) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = Number(digits[i]);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

watch(
  () => props.modelValue,
  (val) => {
    const digits = onlyDigits(val ?? "");
    displayValue.value = formatCard(digits);
    emit("brand", detectBrand(digits));
  },
  { immediate: true }
);

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  const digits = onlyDigits(props.modelValue ?? "");
  return !!digits && !isValidLuhn(digits);
});

const computedStatus = computed(() => props.status || (localError.value ? "error" : ""));

const onInput = (val: string) => {
  const digits = onlyDigits(val);
  displayValue.value = formatCard(digits);
  const emitValue = props.unmasked ? digits : displayValue.value;
  emit("update:modelValue", emitValue);
  emit("change", emitValue);
  emit("brand", detectBrand(digits));
};

const onBlur = (e: FocusEvent) => {
  emit("validate", isValidLuhn(onlyDigits(props.modelValue ?? "")));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);

return (_ctx: any,_cache: any) => {
  const _component_a_input = _resolveComponent("a-input")!

  return (_openBlock(), _createBlock(_component_a_input, {
    value: displayValue.value,
    "onUpdate:value": [
      _cache[0] || (_cache[0] = ($event: any) => ((displayValue).value = $event)),
      onInput
    ],
    placeholder: _ctx.placeholder,
    disabled: _ctx.disabled,
    size: _ctx.size,
    "allow-clear": _ctx.allowClear,
    maxlength: 19,
    status: computedStatus.value,
    class: "ant-credit-card-input",
    onBlur: onBlur,
    onPressEnter: onPressEnter
  }, {
    prefix: _withCtx(() => [
      _createElementVNode("i", {
        class: _normalizeClass(brandIcon.value)
      }, null, 2)
    ]),
    _: 1
  }, 8, ["value", "placeholder", "disabled", "size", "allow-clear", "status"]))
}
}

})